import { lineMode, rpsCutCents } from "./extraction";
import { formatUsd, sumCents } from "./money";
import type { Coverage, EstimateExtraction, EstimateItem, ProposalBuildRequest, ProposalInput, ProposalLine, ProposalRep, ProposalWarning, ScopeGroup } from "./types";
import { CODE_UPGRADE_GROUP, OTHER_PRODUCT_FALLBACK_NAME, OTHER_PRODUCT_SELECTIONS, PRODUCTS } from "./wording";

/**
 * Turning a checked carrier estimate plus the rep's choices into a proposal. Ported from the
 * Worker's `buildProposal()` (src/estimate.ts), including how sales tax and O&P are split
 * between NMT's contract and the items NMT isn't doing.
 */

/** Coverages whose money belongs to the homeowner (contents, loss of use / ALE). */
const OWNER_FUNDS = /personal property|contents|loss of use|additional living|\bALE\b/i;

/**
 * Contents / Loss of Use coverage: paid to the homeowner, never part of the proposal's math.
 * True by name, or when every line in it is trade "Contents". Worker `ownerFundsCoverage()`.
 */
export function isOwnerFundsCoverage(cv: Coverage, ex: EstimateExtraction): boolean {
  if (OWNER_FUNDS.test(`${cv.code} ${cv.name}`)) return true;
  const its = ex.items.filter((i) => i.coverage === cv.code);
  return its.length > 0 && its.every((i) => i.trade === "Contents");
}

/** A line that belongs to the homeowner (Contents trade, or in an owner-funds coverage). Hidden from the proposal form's checkboxes. */
export function isOwnerItem(item: EstimateItem, ex: EstimateExtraction): boolean {
  if (item.trade === "Contents") return true;
  const cv = ex.coverages.find((c) => c.code === item.coverage);
  return !!cv && isOwnerFundsCoverage(cv, ex);
}

/** Proposal form default: every non-owner line included. */
export function defaultIncludedLines(ex: EstimateExtraction): number[] {
  return ex.items.filter((i) => !isOwnerItem(i, ex)).map((i) => i.n);
}

/**
 * Proposal form default deductible: the sum of every coverage's "Less Deductible" (including
 * contents coverages, as the Worker's form did; `buildProposal` compares against non-owner
 * coverages only, so a contents deductible shows a warning).
 */
export function defaultDeductibleCents(ex: EstimateExtraction): number {
  return sumCents(ex.coverages, (c) => c.deductibleCents);
}

/** Group key on the form and the AI's group summaries: "<trade> - <building>". */
export function groupKey(it: Pick<EstimateItem, "trade" | "building">): string {
  return `${it.trade} - ${it.building}`;
}

/** Order of trade groups on the proposal; unknown trades sort first (as `indexOf` gives -1 in the Worker). */
export const TRADE_ORDER = ["Roofing", "Gutters & Guards", "Siding", "Fascia & Trim", "Windows", "Doors", "Fence", "Interior", "Contents", "General"];
const buildingRank = (b: string | undefined) => (/main|dwelling|house/i.test(b ?? "") ? 0 : 1);

/** Line description on the proposal: adds the area for elevation / shed / garage / detached lines. Worker `label()`. */
export function lineLabel(it: Pick<EstimateItem, "area" | "description">): string {
  return /elevation|shed|garage|detached/i.test(it.area) && !it.description.includes("(") ? `${it.description} (${it.area})` : it.description;
}

/**
 * How the proposal form shows a line (client-side in the Worker): RPS lines show the RPS cut as
 * non-recoverable depreciation, and ACV after the cut (not floored at $0 here, unlike the proposal).
 */
export function formLineAmounts(i: EstimateItem): { depreciationCents: number; acvCents: number; recoverable: boolean; rpsCutCents: number } {
  const cut = i.rpsTotalCents != null ? Math.max(0, i.rcvCents - i.rpsTotalCents) : 0;
  return { depreciationCents: i.depreciationCents + cut, acvCents: i.acvCents - cut, recoverable: i.recoverable && !cut, rpsCutCents: cut };
}

/** RPS footnotes for the scope page, one per coverage with an RPS block. Worker `rpsNotes()`. */
export function rpsNotes(ex: EstimateExtraction): string[] | undefined {
  const notes: string[] = [];
  for (const cv of ex.coverages) {
    if (!cv.rps) continue;
    const factor = ex.items.find((i) => i.coverage === cv.code && /\d/.test(i.rpsFactor ?? ""))?.rpsFactor;
    notes.push(
      `Roof Surface Payment Schedule (RPS): your policy pays ${factor ? `${factor} of ` : "a scheduled percentage of "}the roof surface replacement cost, based on the age and type of your roof. The rest (${formatUsd(cv.rps.customerPortionCents ?? 0)} on your insurance estimate, including its share of tax and overhead & profit) is your portion and is never paid by your insurer. It is shown as non-recoverable depreciation < > on the roof lines marked "RPS".`,
    );
  }
  return notes.length ? notes : undefined;
}

/** A coverage (or its RPS / non-RPS part) whose tax and O&P are split between contract and excluded. */
interface TaxOpGroup {
  name: string;
  items: EstimateItem[];
  tax: number;
  op: number;
  rcv: number;
  acv: number;
  rec: number;
  nonRec: number;
  rps: boolean;
}

/**
 * Worker `buildProposal()`: the proposal input and warnings for a checked estimate and the rep's
 * choices. Throws when no line is included.
 *
 * - Contents / Loss of Use coverages and Contents lines are removed (one claim note mentions them).
 * - Included lines are grouped by "<trade> - <building>" (main house first, then by trade order);
 *   code-upgrade lines get their own group; General lines (debris, etc.) and the tax / O&P lines go last.
 * - RPS lines: the RPS cut is taken off ACV and the line becomes non-recoverable.
 * - Sales tax and O&P printed only on the coverage summary become proposal lines: all of the tax
 *   goes to the contract when any of that coverage's lines are included; O&P is prorated at the
 *   coverage's O&P rate on the included lines; what isn't contracted goes to "excluded". Their
 *   depreciation is the summary depreciation not on any line, split between tax and O&P.
 */
export function buildProposal(fullEx: EstimateExtraction, req: ProposalBuildRequest, rep: ProposalRep): { input: ProposalInput; warnings: ProposalWarning[] } {
  const warnings: ProposalWarning[] = [];
  const ownerCovs = fullEx.coverages.filter((cv) => isOwnerFundsCoverage(cv, fullEx));
  const ownerCodes = new Set(ownerCovs.map((cv) => cv.code));
  const ownerItem = (i: EstimateItem) => ownerCodes.has(i.coverage) || i.trade === "Contents";
  const ownerFundsTotal = sumCents(ownerCovs, (cv) => cv.rcvCents);
  const ex: EstimateExtraction = { ...fullEx, coverages: fullEx.coverages.filter((cv) => !ownerCodes.has(cv.code)), items: fullEx.items.filter((i) => !ownerItem(i)) };

  const inc = new Set(req.included);
  const contractItems = ex.items.filter((i) => inc.has(i.n));
  const excludedItems = ex.items.filter((i) => !inc.has(i.n));
  const pwiCodes = new Set(ex.coverages.filter((cv) => cv.paidWhenIncurred).map((cv) => cv.code));
  const isPwi = (it: EstimateItem) => pwiCodes.has(it.coverage) || !!it.outsideTotals;
  const li = (it: EstimateItem): ProposalLine => {
    const cut = rpsCutCents(it);
    const line: ProposalLine = {
      description: isPwi(it) ? `${lineLabel(it)} (code upgrade - paid when incurred)` : cut ? `${lineLabel(it)} (RPS ${it.rpsFactor ?? ""})`.replace(" )", ")") : lineLabel(it),
      rcvCents: it.rcvCents,
      acvCents: Math.max(0, it.acvCents - cut),
      recoverable: cut ? false : it.recoverable,
    };
    if (isPwi(it)) line.paidWhenIncurred = true;
    return line;
  };

  const byGroup = new Map<string, EstimateItem[]>();
  for (const it of contractItems.filter((i) => i.trade !== "General" && !isPwi(i))) {
    const k = groupKey(it);
    if (!byGroup.has(k)) byGroup.set(k, []);
    byGroup.get(k)!.push(it);
  }
  const keys = [...byGroup.keys()].sort((a, b) => {
    const [ta, ba] = a.split(" - "), [tb, bb] = b.split(" - ");
    return buildingRank(ba) - buildingRank(bb) || TRADE_ORDER.indexOf(ta!) - TRADE_ORDER.indexOf(tb!) || a.localeCompare(b);
  });
  const scope: ScopeGroup[] = keys.map((k) => {
    const g: ScopeGroup = { heading: req.groups?.[k]?.heading || k, items: byGroup.get(k)!.map(li) };
    const summary = req.groups?.[k]?.summary ?? ex.groupSummaries?.[k];
    if (summary !== undefined) g.summary = summary;
    return g;
  });
  const pwiItems = contractItems.filter(isPwi);
  if (pwiItems.length) scope.push({ ...CODE_UPGRADE_GROUP, items: pwiItems.map((it) => ({ ...li(it), description: lineLabel(it) })) });
  const finalGroup: ProposalLine[] = contractItems.filter((i) => i.trade === "General" && !isPwi(i)).map(li);
  const excludedExtras: ProposalLine[] = [];

  const groups: TaxOpGroup[] = [];
  for (const cv of ex.coverages) {
    const mode = lineMode(ex, cv) ?? { taxIn: false, opIn: false };
    if (mode.taxIn && mode.opIn) continue;
    const all = ex.items.filter((i) => i.coverage === cv.code && !i.outsideTotals);
    const shortName = cv.name.replace(/^[A-Z0-9]{2}-/, "");
    const tax = mode.taxIn ? 0 : cv.taxCents, op = mode.opIn ? 0 : cv.overheadCents + cv.profitCents;
    const base = { rec: cv.recoverableDepreciationCents, nonRec: cv.nonRecoverableDepreciationCents };
    if (cv.rps) {
      const r = cv.rps;
      const rl = all.filter((i) => i.rpsTotalCents != null);
      const rest = all.filter((i) => i.rpsTotalCents == null);
      const rTax = mode.taxIn ? 0 : (r.taxCents ?? 0), rOp = mode.opIn ? 0 : (r.overheadCents ?? 0) + (r.profitCents ?? 0);
      const rpsLineDep = sumCents(rl, (i) => i.depreciationCents);
      const rRcv = r.rcvCents ?? 0;
      const rAcv = (r.rpsValueCents ?? 0) - rpsLineDep;
      groups.push({ name: `${shortName} - roof payment schedule`, items: rl, tax: rTax, op: rOp, rcv: rRcv, acv: rAcv, rec: 0, nonRec: rRcv - rAcv, rps: true });
      groups.push({ name: shortName, items: rest, tax: tax - rTax, op: op - rOp, rcv: cv.rcvCents - rRcv, acv: cv.acvCents - rAcv, ...base, rps: false });
    } else {
      groups.push({ name: shortName, items: all, tax, op, rcv: cv.rcvCents, acv: cv.acvCents, ...base, rps: false });
    }
  }

  for (const g of groups) {
    const { tax, op } = g;
    if (!tax && !op) continue;
    const dep = (i: EstimateItem) => i.depreciationCents + rpsCutCents(i);
    const all = g.items;
    const mine = all.filter((i) => inc.has(i.n));
    const L = sumCents(all, (i) => i.rcvCents);
    const Ld = sumCents(all, dep);
    const Lc = sumCents(mine, (i) => i.rcvCents);
    const Ldc = sumCents(mine, dep);
    // Summary depreciation not on any line = depreciation on tax and O&P.
    const extraDep = Math.max(0, g.rcv - g.acv - Ld);
    // O&P rate on (lines + tax), snapped to the nearest 0.5% when within 0.05%.
    const rawR = L + tax > 0 ? op / (L + tax) : 0;
    const snapped = Math.round(rawR * 200) / 200;
    const r = Math.abs(rawR - snapped) < 5e-4 ? snapped : rawR;
    // Tax depreciation t, with O&P depreciation = r × (line dep + t): t = (extra − r·Ld) / (1 + r).
    const t = Math.min(extraDep, Math.max(0, Math.round((extraDep - r * Ld) / (1 + r))));
    const opDepTotal = extraDep - t;
    const allNonRec = g.rps || (g.rec === 0 && g.rcv - g.acv > 0);
    const exRec = !g.rps && g.nonRec === 0;
    if (mine.length) {
      if (tax) finalGroup.push({ description: `Sales Tax on Materials (${g.name})`, rcvCents: tax, acvCents: tax - t, recoverable: !allNonRec });
      if (op) {
        const allIn = mine.length === all.length;
        const opC = allIn ? op : Math.round(r * (Lc + tax));
        const opCd = allIn ? opDepTotal : Math.min(opDepTotal, Math.round(r * (Ldc + t)));
        finalGroup.push({ description: `Overhead & Profit on Contracted Work (${g.name})`, rcvCents: opC, acvCents: opC - opCd, recoverable: !allNonRec });
        if (op - opC > 0)
          excludedExtras.push({ description: `Overhead & Profit - balance not contracted (${g.name})`, rcvCents: op - opC, acvCents: op - opC - (opDepTotal - opCd), recoverable: exRec });
      }
    } else {
      if (tax) excludedExtras.push({ description: `Sales Tax on Materials (${g.name})`, rcvCents: tax, acvCents: tax - t, recoverable: exRec });
      if (op) excludedExtras.push({ description: `Overhead & Profit (${g.name})`, rcvCents: op, acvCents: op - opDepTotal, recoverable: exRec });
    }
  }
  if (finalGroup.length) scope.push({ heading: finalGroup.some((x) => /tax|overhead/i.test(x.description)) ? "Debris, Tax, Overhead & Profit" : "Debris & General", items: finalGroup });
  if (!scope.length) throw new Error("No line items are included in the contract.");

  const product =
    req.product.key === "other"
      ? { name: req.product.name || OTHER_PRODUCT_FALLBACK_NAME, description: req.product.description || "", selections: OTHER_PRODUCT_SELECTIONS }
      : PRODUCTS[req.product.key];
  const claimRec = sumCents(ex.coverages, (cv) => cv.recoverableDepreciationCents);
  const upgradeItems = req.upgrades?.items ?? [];
  const noCharge = req.upgrades?.noCharge ?? [];

  const input: ProposalInput = {
    customer: req.customer,
    claim: { carrier: ex.claim.carrier, claimNumber: ex.claim.claimNumber, policyNumber: ex.claim.policyNumber, dateOfLoss: ex.claim.dateOfLoss },
    deductibleCents: req.deductibleCents,
    system: product.description ? { name: product.name, description: product.description } : { name: product.name },
    scope,
    excluded: [...excludedItems.map(li), ...excludedExtras],
    claimRecoverableDepCents: claimRec,
    productSelections: [...product.selections],
    preparedBy: rep,
  };
  if (req.date !== undefined) input.date = req.date;
  if (req.additionalTerms) input.additionalTerms = req.additionalTerms.filter((x) => x.trim());
  const scopeNotes = rpsNotes(ex);
  if (scopeNotes) input.scopeNotes = scopeNotes;
  if (ownerFundsTotal > 0) {
    const what = ownerCovs
      .map((cv) => (/loss of use|additional living|\bALE\b/i.test(cv.name) ? "additional living expenses" : "contents (personal property)"))
      .filter((v, i, a) => a.indexOf(v) === i)
      .join(" and ");
    input.claimNotes = [
      `Your insurance claim also includes ${formatUsd(ownerFundsTotal)} for ${what}. That money is paid to you separately and is not part of this proposal or the numbers above.`,
    ];
  }
  // As in the Worker, an upgrade total counts only when at least one upgrade or no-charge item is listed.
  if (upgradeItems.length + noCharge.length > 0) {
    input.upgrades = { items: upgradeItems.filter((u) => u.description.trim()), noCharge: noCharge.filter((x) => x.trim()), totalCents: req.upgrades?.totalCents ?? 0 };
  }

  const tot = (xs: ProposalLine[]) => sumCents(xs, (x) => x.rcvCents);
  const contractRcv = sumCents(scope, (g) => tot(g.items));
  const excludedRcv = tot(input.excluded!);
  const claimRcv = sumCents(ex.coverages, (cv) => cv.rcvCents) + sumCents(ex.items.filter((i) => i.outsideTotals), (i) => i.rcvCents);
  if (contractRcv + excludedRcv !== claimRcv)
    warnings.push({
      code: "claim_mismatch",
      message: `Contract + excluded (${formatUsd(contractRcv + excludedRcv)}) doesn't equal the claim (${formatUsd(claimRcv)}). Check before sending.`,
    });
  const covDed = sumCents(ex.coverages, (cv) => cv.deductibleCents);
  if (covDed && covDed !== req.deductibleCents)
    warnings.push({ code: "deductible_mismatch", message: `Deductible entered (${formatUsd(req.deductibleCents)}) differs from the estimate (${formatUsd(covDed)}).` });
  return { input, warnings };
}

/** The Worker refused to send a proposal whose contract + excluded didn't equal the claim. */
export function warningsBlockSending(warnings: readonly ProposalWarning[]): boolean {
  return warnings.some((w) => w.code === "claim_mismatch");
}
